import { useEffect, useRef, useState } from "react";
import { loadHubSpotSdk } from "~/components/forms/HubSpotForm";

/**
 * C1 Transform 2026 VIP / speaker registration form.
 *
 * Same approach as TransformRegister.tsx: embeds the real HubSpot form
 * (native `hbspt.forms.create`, "no style" in the portal) so bot protection,
 * validation, and property definitions all apply for real, then re-skins the
 * raw markup with CSS. Sent by direct invite only, never linked publicly --
 * there's no gate and no approval step, the seat is already held.
 *
 * Unlike the public register form, this is one HubSpot form visually split
 * into two cards ("You" and "Hotel & travel") via a DOM touch-up in
 * onFormReady, the same technique TransformRegister.tsx already uses to
 * group its opt-in checkboxes into a shaded panel -- just generalized to two
 * buckets of fields instead of one. Splitting this into two separate HubSpot
 * forms was considered and rejected: the design has a single "Submit
 * details" button for the whole page, and two independent submissions would
 * need to be stitched onto the same contact record (via a duplicated,
 * JS-copied email field) with no good story for a partial failure -- the
 * same class of fragility the public form's abandoned two-step wizard ran
 * into, see the comment at the top of TransformRegister.tsx.
 *
 * Also unlike the public form, submitting shows an inline "you're all set"
 * confirmation card in place of the form rather than redirecting to a
 * thank-you route: there's no review queue to explain, so the confirmation
 * can just state the (already-held) seat and what happens next.
 */

// Field names match the real HubSpot form (portal 9161697, form
// 3774de4e-8042-4072-81da-3ccf7cca0f4c) as field marketing configured it --
// not the placeholder names the original design mockup's raw HTML used.
const CARD_1_FIELD_NAMES = new Set([
  "firstname",
  "lastname",
  "email",
  "ea_email",
  "company",
  "jobtitle",
  "phone",
  "linkedin_profile_url",
  "are_you_a_c1_customer",
  "tshirt_size",
  // Reuses the public register form's dietary/accessibility fields (a
  // checkbox group with an "Other" dependent field, plus a separate
  // textarea) rather than the mockup's single combined field.
  "c1_transform_dietary_restrictions",
  "c1_transform_accessibility_requirements",
]);

const EVENT_FIELD_NAMES = new Set(["welcome_party", "dinner_reception"]);

const HOTEL_DATE_FIELD_NAMES = new Set(["hotel_checkin_date", "hotel_checkout_date"]);

// Context for each hotel date so a guest doesn't have to cross-reference the
// event schedule elsewhere on the page to pick the right dates.
//
// Only the hotel dates live here. Anything else wanting help text should get a
// description on the property in the portal instead -- `.hs-field-desc` is
// styled below, so portal copy renders correctly and stays owned by whoever
// owns the form.
const HOTEL_DATE_HELP: Record<string, string> = {
  hotel_checkin_date: "The Welcome Party takes place Monday, October 5.",
  hotel_checkout_date:
    "The dinner reception ends around 9:00pm on October 6 — we recommend checking out October 7.",
};

const CARD_2_FIELD_NAMES = new Set([
  ...HOTEL_DATE_FIELD_NAMES,
  "traveler_name",
  "departing_airport_name_and_code",
  "preferred_airline",
  "frequent_flyer_number",
  "preferred_seat",
  "tsa_precheck",
  "global_entry",
  "preferred_arriving_flight_into_sfo",
  "preferred_departing_flight_from_sfo",
  "additional_travel_notes",
]);

/** Flight fields pinned to the top of the Flights subsection, in this order,
 *  ahead of whatever order the portal renders the rest in. */
const FLIGHT_LEAD_FIELD_NAMES = ["traveler_name"];

interface TransformVipRegisterProps {
  portal_id?: string;
  form_id: string;
  event_date_label?: string;
  venue_name?: string;
  venue_address?: string;
  event_details_url?: string;
}

function fieldControlName(field: Element): string | null {
  // [name], not "input, select, textarea" -- HubSpot's date field type renders
  // a nameless readonly display input (the calendar-picker text box) plus a
  // hidden input that actually carries the name/value. Matching any element
  // with a name skips straight to whichever one actually has it.
  return field.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[name]")?.name ?? null;
}

function makeSectionHeading(eyebrow: string, title?: string): HTMLDivElement {
  const wrap = document.createElement("div");
  wrap.className = "transform-vip-section-heading";
  const eyebrowEl = document.createElement("div");
  eyebrowEl.className = "transform-vip-eyebrow";
  eyebrowEl.textContent = eyebrow;
  wrap.appendChild(eyebrowEl);
  if (title) {
    const titleEl = document.createElement("div");
    titleEl.className = "transform-vip-section-title";
    titleEl.textContent = title;
    wrap.appendChild(titleEl);
  }
  return wrap;
}

function makeGrid(fields: HTMLElement[]): HTMLDivElement {
  const grid = document.createElement("div");
  grid.className = "transform-vip-card-grid";
  fields.forEach((field) => grid.appendChild(field));
  return grid;
}

function appendFieldHelp(field: HTMLElement, text: string): void {
  const help = document.createElement("div");
  help.className = "transform-vip-field-help";
  help.textContent = text;
  field.appendChild(help);
}

/**
 * Light DOM touch-up on HubSpot's rendered form: regroup the flat field list
 * into the two-card layout the design calls for. Pure DOM work on a subtree
 * HubSpot's own script injected, not React-rendered -- no field hiding, no
 * custom validation gate, HubSpot's own Submit and validation still behave
 * completely natively. A no-op if the fields it looks for aren't present
 * (e.g. against a form that isn't configured with these properties yet).
 */
function layoutFormIntoCards(container: HTMLDivElement): void {
  const form = container.querySelector("form");
  if (!form) return;

  const topLevelFields = Array.from(
    form.querySelectorAll<HTMLElement>(":scope > .hs-form-field, :scope > .hs-dependent-field"),
  );

  const card1Fields: HTMLElement[] = [];
  const eventFields: HTMLElement[] = [];
  const hotelFields: HTMLElement[] = [];
  const flightFields: HTMLElement[] = [];

  for (const field of topLevelFields) {
    const name = fieldControlName(field);
    if (!name) continue;
    if (EVENT_FIELD_NAMES.has(name)) eventFields.push(field);
    else if (CARD_1_FIELD_NAMES.has(name)) card1Fields.push(field);
    else if (HOTEL_DATE_FIELD_NAMES.has(name)) hotelFields.push(field);
    else if (CARD_2_FIELD_NAMES.has(name)) flightFields.push(field);
  }

  /* Order inside a section is otherwise whatever order the portal renders the
     fields in, because the bucketing above just preserves DOM order. That is
     fine for fields whose grouping and sequence were designed together, but
     "Traveler Full Name" was added to the form after the flight fields, so
     HubSpot emits it last -- and it needs to be read and filled FIRST, since
     every field after it describes that traveler's itinerary.

     Pinned here rather than reordered in the portal so the two stay
     independent: field marketing can add or reorder portal fields without
     silently dropping this one to the bottom of the section. Sort is stable,
     so everything not pinned keeps the portal's order. */
  const flightLeadRank = (field: HTMLElement): number => {
    const name = fieldControlName(field);
    const rank = name ? FLIGHT_LEAD_FIELD_NAMES.indexOf(name) : -1;
    return rank === -1 ? FLIGHT_LEAD_FIELD_NAMES.length : rank;
  };
  flightFields.sort((a, b) => flightLeadRank(a) - flightLeadRank(b));

  if (card1Fields.length === 0 && hotelFields.length === 0 && flightFields.length === 0) return;

  const card1 = document.createElement("div");
  card1.className = "transform-vip-card";
  card1.appendChild(makeSectionHeading("SECTION 1 OF 2"));
  card1.appendChild(makeGrid(card1Fields));

  if (eventFields.length > 0) {
    const eventsWrap = document.createElement("div");
    eventsWrap.className = "transform-vip-events";
    const eventsHeading = document.createElement("div");
    eventsHeading.className = "transform-vip-events-heading";
    eventsHeading.textContent =
      "Please let us know if you're able to join us for these evening events:";
    eventsWrap.appendChild(eventsHeading);
    eventFields.forEach((field) => {
      field.classList.add("transform-vip-event-field");
      eventsWrap.appendChild(field);
    });
    card1.appendChild(eventsWrap);
  }

  const card2 = document.createElement("div");
  card2.className = "transform-vip-card";
  const card2Heading = makeSectionHeading("SECTION 2 OF 2 · OPTIONAL", "Hotel & travel");
  const card2Intro = document.createElement("div");
  card2Intro.className = "transform-vip-card-intro";
  card2Intro.textContent =
    "Skip this whole section if you're local or making your own arrangements.";
  card2Heading.appendChild(card2Intro);
  card2.appendChild(card2Heading);

  if (hotelFields.length > 0) {
    hotelFields.forEach((field) => {
      const name = fieldControlName(field);
      const help = name ? HOTEL_DATE_HELP[name] : undefined;
      if (help) appendFieldHelp(field, help);
    });
    const hotelWrap = document.createElement("div");
    hotelWrap.className = "transform-vip-subsection";
    hotelWrap.appendChild(makeGrid(hotelFields));
    card2.appendChild(hotelWrap);
  }

  if (flightFields.length > 0) {
    const flightWrap = document.createElement("div");
    flightWrap.className = "transform-vip-subsection transform-vip-subsection-divider";
    const flightLabel = document.createElement("div");
    flightLabel.className = "transform-vip-subsection-label";
    flightLabel.textContent = "Flights";
    flightWrap.append(flightLabel, makeGrid(flightFields));
    card2.appendChild(flightWrap);
  }

  const cardsWrap = document.createElement("div");
  cardsWrap.className = "transform-vip-cards";
  cardsWrap.append(card1, card2);
  form.prepend(cardsWrap);
}

/** Reads the two hotel date inputs the visitor actually filled in, for the confirmation card. */
function readHotelStaySummary(container: HTMLDivElement): string | null {
  const checkIn = container.querySelector<HTMLInputElement>('input[name="hotel_checkin_date"]')?.value;
  const checkOut = container.querySelector<HTMLInputElement>('input[name="hotel_checkout_date"]')?.value;
  if (!checkIn || !checkOut) return null;
  const inDate = new Date(checkIn);
  const outDate = new Date(checkOut);
  const nights = Math.round((outDate.getTime() - inDate.getTime()) / 86_400_000);
  if (!Number.isFinite(nights) || nights <= 0) return null;
  // timeZone: "UTC" -- the inputs are date-only strings (no time component),
  // parsed above as UTC midnight. Formatting in the viewer's local zone
  // instead would roll the date back a day for anyone west of UTC (i.e.
  // most of the US, including the SF event itself).
  const monthDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  const dayOnly = new Intl.DateTimeFormat("en-US", { day: "numeric", timeZone: "UTC" });
  const sameMonth = inDate.getUTCMonth() === outDate.getUTCMonth() && inDate.getUTCFullYear() === outDate.getUTCFullYear();
  const range = sameMonth ? `${monthDay.format(inDate)}–${dayOnly.format(outDate)}` : `${monthDay.format(inDate)}–${monthDay.format(outDate)}`;
  return `${range}, ${nights} night${nights === 1 ? "" : "s"}`;
}

export function TransformVipRegister({
  portal_id = "9161697",
  form_id,
  event_date_label = "October 5–6, 2026",
  venue_name = "The Clancy",
  venue_address = "San Francisco",
  event_details_url = "/c1-transform-2026",
}: TransformVipRegisterProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const formCreated = useRef(false);
  const [submitted, setSubmitted] = useState(false);
  const [staySummary, setStaySummary] = useState("Oct 5–7, 2 nights");

  const targetId = `hsform-transform-vip-register-${form_id.replace(/[^a-zA-Z0-9]/g, "")}`;

  useEffect(() => {
    if (!form_id || formCreated.current || typeof window === "undefined") return;
    formCreated.current = true;

    loadHubSpotSdk().then(() => {
      if (!window.hbspt || !containerRef.current) return;
      window.hbspt.forms.create({
        region: "na1",
        portalId: portal_id,
        formId: form_id,
        target: `#${targetId}`,
        onFormReady: () => {
          if (containerRef.current) layoutFormIntoCards(containerRef.current);
        },
        onFormSubmitted: () => {
          if (containerRef.current) {
            const summary = readHotelStaySummary(containerRef.current);
            if (summary) setStaySummary(summary);
          }
          setSubmitted(true);
        },
      });
    });
  }, [form_id, portal_id, targetId]);

  const eventLine = [event_date_label, [venue_name, venue_address].filter(Boolean).join(", ")]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <style>{HUBSPOT_FORM_CSS}</style>

      <div className="flex items-center justify-between gap-4 border-b border-teal-dark/10 px-8 py-[18px]">
        <a href="/" className="flex shrink-0 items-center">
          <img src="/images/svg/c1-ai-logo-dark.svg" alt="C1" className="h-[22px] w-auto" />
        </a>
        <div className="font-display text-[11px] font-bold uppercase tracking-[0.12em] text-gray-8">
          C1 Transform 2026 · VIP &amp; speaker registration
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center gap-10 px-6 py-14 sm:py-16">
        <div className="flex max-w-3xl flex-col items-center gap-4 text-center">
          <span className="inline-block bg-pink-accent px-2.5 py-1 font-display text-[11px] font-bold uppercase tracking-[0.12em] text-dark">
            By invitation
          </span>
          <h1 className="text-balance font-display text-[clamp(33px,6vw,50px)] font-bold leading-none tracking-tight text-teal-dark">
            Confirm your details for C1 Transform
          </h1>
          {eventLine && (
            <p className="text-[clamp(17px,2.4vw,21px)] tracking-tight text-slate">{eventLine}</p>
          )}
          <p className="max-w-[580px] text-pretty text-base leading-relaxed text-slate">
            We look forward to seeing you.
            <br />
            C1.ai will arrange your hotel and flights using your details below.
          </p>
        </div>

        {!submitted ? (
          <div className="w-full max-w-[760px]">
            <div id={targetId} ref={containerRef} className="transform-vip-hs-form" />
          </div>
        ) : (
          <div className="flex w-full max-w-[640px] flex-col items-center gap-6 bg-teal-dark px-6 py-10 text-center sm:px-10 sm:py-12">
            <span className="inline-block bg-[#8AF1D9] px-2.5 py-1 font-display text-[11px] font-bold uppercase tracking-[0.12em] text-teal-dark">
              Confirmed
            </span>
            <h2 className="font-display text-[clamp(30px,5vw,38px)] font-bold leading-tight tracking-tight text-cream">
              You&apos;re all set
            </h2>
            <p className="max-w-[460px] text-pretty text-[17px] leading-relaxed text-cream/80">
              Your seat is confirmed for {event_date_label} at {venue_name}. If you provided your travel
              information, our team will reach out with hotel and flight options.
            </p>
            <div className="w-full max-w-[460px] border-t border-cream/20">
              <div className="flex items-center justify-between gap-4 border-b border-cream/20 py-[13px]">
                <span className="font-display text-[11px] font-bold tracking-[0.12em] text-cream/55">HOTEL</span>
                <span className="text-[15px] text-cream">{staySummary}</span>
              </div>
              <div className="flex items-center justify-between gap-4 border-b border-cream/20 py-[13px]">
                <span className="font-display text-[11px] font-bold tracking-[0.12em] text-cream/55">FLIGHTS</span>
                <span className="text-[15px] text-cream">Roundtrip into SFO</span>
              </div>
            </div>
            <a
              href={event_details_url}
              className="rounded-[10px] border border-cream px-6 py-[11px] text-base font-medium text-cream no-underline"
            >
              Return to event page
            </a>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-teal-dark/10 px-6 py-6 sm:px-8">
        <div className="text-xs text-gray-8">C1.ai · San Francisco, CA</div>
        <div className="flex flex-wrap gap-4">
          <a href={event_details_url} className="text-xs text-teal-dark hover:text-teal-dark-hover">
            Event details
          </a>
          <a href="/privacy-policy" className="text-xs text-teal-dark hover:text-teal-dark-hover">
            Privacy policy
          </a>
        </div>
      </div>
    </div>
  );
}

// HubSpot's form is configured with "no style" (raw HTML) in the portal, so
// none of its own theme CSS ships -- this is the entire visual layer for the
// native embed's structural class names (.hs-form-field, .hs-input, etc.)
// plus the .transform-vip-* classes layoutFormIntoCards() injects above.
const HUBSPOT_FORM_CSS = `
.transform-vip-hs-form form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: var(--font-body);
}
.transform-vip-cards {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.transform-vip-card {
  background: #ffffff;
  border: 1px solid #DCDADA;
  padding: clamp(24px, 4vw, 40px);
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.transform-vip-section-heading {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.transform-vip-eyebrow {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.12em;
  /* gray-8 (#6F6F71, 5.01:1 on white) rather than the gray-60 (#8B8990) this
     used: every use of the muted colour on this page is 11-13px text, where
     4.5:1 is the WCAG AA floor, and #8B8990 is 3.45:1 -- it failed. Swapped
     locally, not in the token, which is used site-wide and belongs to the
     design system. Applied to the field help, portal descriptions and richtext
     below too; all had the same problem. */
  color: var(--color-gray-8);
}
.transform-vip-section-title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: clamp(23px, 3.4vw, 28px);
  letter-spacing: -0.01em;
  color: var(--color-teal-dark);
}
.transform-vip-card-intro {
  font-family: var(--font-body);
  font-size: 16px;
  /* Bold because this line is doing real work: it tells a local guest they can
     skip the entire section below, and at the default weight in muted slate it
     read as decoration and got missed. */
  font-weight: 700;
  line-height: 1.55;
  color: var(--color-slate);
  margin-top: 6px;
}
.transform-vip-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
}
.transform-vip-card-grid .hs_c1_transform_accessibility_requirements,
/* traveler_name spans both columns because it carries a portal description, and
   HubSpot renders a description BETWEEN the label and the input -- which pushes
   its input ~21px below the input of whatever half-width field shares its row,
   so the two boxes visibly fail to line up. A full-width row has nothing beside
   it to misalign against.

   It also happens to tidy the section: with this field taking a row of its own,
   the eight remaining half-width flight fields pair off exactly, instead of
   leaving a lone half-width field stranded in the last row. And as the field
   the rest of the section describes an itinerary for, leading on its own line
   reads correctly.

   Any future field given a portal description has the same alignment problem;
   the general fix would be reordering the description below the input, which
   is deliberately not attempted here because .hs-form-field also wraps checkbox
   groups, dependent fields and error messages. */
.transform-vip-card-grid .hs_traveler_name,
.transform-vip-card-grid .hs_additional_travel_notes,
.transform-vip-card-grid .hs-dependent-field {
  grid-column: 1 / -1;
}
/* c1_transform_dietary_restrictions has an "Other" dependent field (a
   follow-up "please specify" text input). HubSpot wraps a controller field
   that has a dependent field in .hs-dependent-field, and that wrapper -- not
   the controller field itself -- is the real grid item, hence the
   grid-column rule above. The controller and the revealed field are plain
   block siblings inside that one grid cell, so they don't get the grid's
   row gap between them -- flex+gap here reproduces that same rhythm. */
.transform-vip-hs-form .hs-dependent-field {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.transform-vip-events {
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-top: 1px solid #DCDADA;
  padding-top: 24px;
}
.transform-vip-events-heading {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 12px;
  color: var(--color-teal-dark);
}
.transform-vip-hs-form .hs-form-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.transform-vip-subsection {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.transform-vip-subsection-divider {
  border-top: 1px solid #DCDADA;
  padding-top: 26px;
}
.transform-vip-subsection-label {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 12px;
  color: var(--color-teal-dark);
}
.transform-vip-hs-form .transform-vip-field-help {
  font-family: var(--font-body);
  font-size: 12px;
  line-height: 1.45;
  color: var(--color-gray-8);
  margin-top: 4px;
}
/* The site-wide .hs-fieldtype-text/-phonenumber/-select rules visually hide
   labels (sr-only clip) in favor of placeholder text -- fine for a plain text
   field, but a <select>'s "Please Select" default option carries no context
   on its own, and this form has far too many fields to rely on placeholder
   text alone. This form always shows real labels, matching the design; the
   extra .transform-vip-hs-form class gives these enough specificity to win. */
.transform-vip-hs-form .hs-form-field > label {
  position: static;
  width: auto;
  height: auto;
  padding: 0;
  margin: 0;
  overflow: visible;
  clip: auto;
  white-space: normal;
  display: block;
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 12px;
  color: var(--color-teal-dark);
}
.transform-vip-hs-form .hs-form-field .hs-form-required {
  color: var(--color-teal-dark);
}
.transform-vip-hs-form .hs-field-desc {
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--color-gray-8);
  margin-top: -3px;
  line-height: 1.45;
}
.transform-vip-hs-form .input {
  width: 100%;
}
.transform-vip-hs-form .hs-input:not([type="checkbox"]):not([type="radio"]) {
  box-sizing: border-box;
  width: 100%;
  border: 1px solid #DCDADA;
  border-radius: 8px;
  /* background-color, not the background shorthand -- the shorthand resets
     background-image to none, wiping out the sitewide select chevron
     (select.hs-input in app.css) that would otherwise show through here. */
  background-color: var(--color-cream);
  padding: 12px 14px;
  font-size: 15px;
  color: var(--color-dark);
  outline: none;
  font-family: var(--font-body);
}
.transform-vip-hs-form select.hs-input:not([type="checkbox"]):not([type="radio"]) {
  padding-right: 2.5rem;
}
.transform-vip-hs-form .hs-input:focus {
  border-color: var(--color-teal-dark);
}
.transform-vip-hs-form textarea.hs-input {
  resize: vertical;
}
.transform-vip-hs-form .hs-form-booleancheckbox-display {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
}
.transform-vip-hs-form .hs-form-booleancheckbox-display > span {
  font-family: var(--font-body);
  font-weight: 700;
  font-size: 15px;
  color: var(--color-teal-dark);
}
/* Multi-option checkbox lists (e.g. dietary restrictions) get plain option
   styling -- the bold/teal title treatment above is specifically for the
   two event opt-in boxes, not a 7-option checkbox list. */
.transform-vip-hs-form .hs-form-checkbox-display {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 400;
  color: var(--color-dark);
  cursor: pointer;
}
.transform-vip-hs-form .hs-form-booleancheckbox input[type="checkbox"],
.transform-vip-hs-form .hs-form-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  accent-color: var(--color-teal-dark);
  flex: none;
}
/* flex+gap here (not just a list-style reset) so a multi-option checkbox
   group gets spacing between its <li> options; a no-op for the single-item
   boolean checkbox lists elsewhere. */
.transform-vip-hs-form .inputs-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
/* The two event checkboxes get their own bordered box (not the public
   form's single shared shaded panel) since each carries its own title +
   description, per the design. */
.transform-vip-hs-form .transform-vip-event-field {
  background: var(--color-cream);
  border: 1px solid #DCDADA;
  padding: 16px 18px;
}
.transform-vip-hs-form .transform-vip-event-field label {
  margin: 0;
}
.transform-vip-hs-form .hs-error-msgs {
  list-style: none;
  padding: 0;
  margin: 4px 0 0;
}
.transform-vip-hs-form .hs-error-msgs label {
  color: #B3261E;
  font-size: 13px;
  font-weight: 400;
}
.transform-vip-hs-form .hs-richtext {
  font-size: 13px;
  color: var(--color-gray-8);
}
.transform-vip-hs-form .hs-richtext p {
  margin: 0;
  line-height: 1.5;
}
.transform-vip-hs-form .hs-richtext a {
  color: var(--color-teal-dark);
  text-decoration: underline;
}
.transform-vip-hs-form .hs-richtext a:hover {
  color: var(--color-teal-dark-hover);
}
.transform-vip-hs-form .hs_submit .actions {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
}
.transform-vip-hs-form .hs-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 18px;
  padding: 12px 32px;
  border: none;
  border-radius: 10px;
  background: var(--color-teal-cta);
  color: var(--color-cream);
  cursor: pointer;
}
.transform-vip-hs-form .hs-button:hover {
  background: var(--color-teal-cta-hover);
}
`;
